/**
 * Bloques comerciales de Mi cartera (Ronda 2.1 — reemplaza las 5 pestañas
 * de la Ronda 2, que quedaban completamente redundantes frente a los
 * bloques: "Respondieron"/"Por vencer"/"Prioridad alta" eran exactamente
 * los mismos tres criterios de abajo, solo que como pestañas que había que
 * clickear en vez de secciones siempre visibles).
 *
 * Opera exclusivamente sobre oportunidades YA CARGADAS por Wallet.jsx
 * (nunca hace una llamada nueva a storage, nunca recalcula responseStatus,
 * outcome, priority ni windowDays) — es lógica de presentación pura, lee
 * campos que el motor correspondiente ya calculó y persistió.
 *
 * Cascada de un solo paso, primer bloque que aplica se la queda -- por
 * diseño ninguna oportunidad puede caer en dos bloques (ver
 * classifyIntoBlocks / walletSegments.test.js "exclusividad mutua").
 */

export const WALLET_BLOCKS = [
  { key: "resume", label: "Conversaciones por retomar" },
  { key: "expiring", label: "Próximas a vencer" },
  { key: "high_priority", label: "Prioridad alta" },
  { key: "rest", label: "El resto de tu cartera" },
];

const EXPIRING_WINDOW_DAYS = 14;

const isOpen = (o) => o.outcome === "pending" || o.outcome === "in_progress";

/**
 * Bloque "Conversaciones por retomar": exige outcome EXACTAMENTE "pending",
 * no "in_progress" -- verificado contra el código real antes de decidir
 * esto (auditoría Ronda 2.1). responseStatus:"replied"/"interested" por sí
 * solo no distingue "el cliente respondió y nadie ha hecho nada desde
 * entonces" de "el cliente respondió, el distribuidor ya lo retomó por
 * teléfono/WhatsApp personal, pero el sistema no tiene forma de saberlo"
 * (OpportunityDetail.jsx ya lo documenta: responseStatus es manual y
 * "solo refleja lo que el usuario ya sabe que pasó fuera del sistema").
 * outcome:"in_progress" SÍ es una señal real y ya existente de que el
 * distribuidor decidió a mano que ya está trabajando el trato -- excluirlo
 * aquí evita la falsa urgencia sin inventar ningún campo ni umbral nuevo.
 */
function blockKeyFor(o) {
  if (!isOpen(o)) return null;
  if (o.outcome === "pending" && ["replied", "interested"].includes(o.responseStatus)) {
    return "resume";
  }
  if (o.engineId === "renewal" && o.windowDays != null && o.windowDays <= EXPIRING_WINDOW_DAYS) {
    return "expiring";
  }
  if (o.priority === "high") {
    return "high_priority";
  }
  return "rest";
}

export function classifyIntoBlocks(opportunities) {
  const buckets = { resume: [], expiring: [], high_priority: [], rest: [] };
  for (const o of opportunities || []) {
    const key = blockKeyFor(o);
    if (key) buckets[key].push(o);
  }
  return buckets;
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

/**
 * Orden de "lista de trabajo priorizada": prioridad alta primero, luego lo
 * más próximo a vencer/atender, luego mayor valor estimado. Mismos campos
 * que ya usa lib/priority.js para Home — ningún criterio nuevo, solo
 * aplicado a la vista completa de Mi cartera en vez de a una sola próxima
 * conversación.
 */
export function sortForWallet(opportunities) {
  return [...(opportunities || [])].sort((a, b) => {
    const rankDiff = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
    if (rankDiff !== 0) return rankDiff;
    const windowDiff = (a.windowDays ?? Infinity) - (b.windowDays ?? Infinity);
    if (windowDiff !== 0) return windowDiff;
    return (b.estimatedValue?.value.amount ?? 0) - (a.estimatedValue?.value.amount ?? 0);
  });
}
